import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  InventoryItem, 
  inventoryCategoryOptions, 
  inventoryStatusOptions, 
  getInventoryStatusColor 
} from "./inventoryTypes";
import { fetchInventoryItems } from "./inventoryService";
import { CheckCircle, Tag, CircleDollarSign, PackageX, Search } from "lucide-react";
import { InventoryItemDialog } from "./InventoryItemDialog";
import { NewInventoryItemDialog } from "./NewInventoryItemDialog";

export function InventoryList() {
  const { data: inventoryItems = [], isLoading, error, refetch } = useQuery({
    queryKey: ["inventoryItems"],
    queryFn: fetchInventoryItems,
  });

  const [filters, setFilters] = useState({
    search: "",
    category: "all",
    status: "all",
  });

  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [showNewItemDialog, setShowNewItemDialog] = useState(false);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filteredItems = inventoryItems.filter((item) => {
    const searchMatch =
      !filters.search ||
      (item.name?.toLowerCase() || "").includes(filters.search.toLowerCase()) ||
      item.serial.toLowerCase().includes(filters.search.toLowerCase()) ||
      (item.description?.toLowerCase() || "").includes(filters.search.toLowerCase()) ||
      (item.assignee_name?.toLowerCase() || "").includes(filters.search.toLowerCase()) ||
      (item.location?.toLowerCase() || "").includes(filters.search.toLowerCase());

    const categoryMatch = filters.category === "all" || item.category === filters.category;
    const statusMatch = filters.status === "all" || item.status === filters.status;

    return searchMatch && categoryMatch && statusMatch;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Available':
        return <CheckCircle className="h-4 w-4" />;
      case 'Assigned':
        return <Tag className="h-4 w-4" />;
      case 'Sold':
        return <CircleDollarSign className="h-4 w-4" />;
      case 'Retired':
        return <PackageX className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const handleItemClick = (item: InventoryItem) => {
    setSelectedItem(item);
  };

  const handleNewItemDialogClose = (created: boolean) => {
    setShowNewItemDialog(false);
    if (created) {
      refetch();
    }
  };

  const handleItemDialogClose = (updated: boolean) => {
    setSelectedItem(null);
    if (updated) {
      refetch();
    }
  };

  if (isLoading) return <div className="p-8 text-center">Loading inventory items...</div>;

  if (error) return <div className="p-8 text-center text-red-500">Error loading inventory items</div>;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search name, serial, description..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        
        <Select
          value={filters.category}
          onValueChange={(value) => handleFilterChange("category", value)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {inventoryCategoryOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select
          value={filters.status}
          onValueChange={(value) => handleFilterChange("status", value)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {inventoryStatusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button onClick={() => setShowNewItemDialog(true)}>New Item</Button>
      </div>

      {/* Inventory Table */}
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Serial</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Assignee/Location</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead className="text-right">Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-4">
                  No inventory items found
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow 
                  key={item.id} 
                  onClick={() => handleItemClick(item)}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  <TableCell className="font-medium">{item.name || "-"}</TableCell>
                  <TableCell>{item.category === "Hardware" ? item.serial : "-"}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>
                    <Badge 
                      variant="outline"
                      className={`${getInventoryStatusColor(item.status)} flex items-center gap-1`}
                    >
                      {getStatusIcon(item.status)} {item.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {item.assignee_name || item.location || "-"}
                  </TableCell>
                  <TableCell>
                    {item.payment_type === "Recursive" ? (
                      <span className="text-sm">
                        {item.payment_cadence} (${item.payment_amount})
                      </span>
                    ) : (
                      "One Time"
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {item.cost ? `${item.currency} ${item.cost}` : "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialogs */}
      {selectedItem && (
        <InventoryItemDialog 
          item={selectedItem} 
          open={!!selectedItem} 
          onClose={handleItemDialogClose} 
        />
      )}

      {showNewItemDialog && (
        <NewInventoryItemDialog
          open={showNewItemDialog}
          onClose={handleNewItemDialogClose}
        />
      )}
    </div>
  );
}
